import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import HeroVideo from "@/components/HeroVideo";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import SolutionFinder from "@/components/SolutionFinder";
import { SERVICES, IMAGES } from "@/lib/site";

const STEPS = [
  {
    title: "Understand",
    body: "Discuss your business, priorities, and challenges. We listen first — no pitches, no pressure.",
  },
  {
    title: "Connect",
    body: "Identify relevant solutions and introduce the appropriate specialists from our provider network.",
  },
  {
    title: "Move forward",
    body: "Clarify the next steps, responsibilities, and implementation process — so you always know where things stand.",
  },
];

function ServiceCard({ s, featured = false }) {
  return (
    <Link
      to={`/solutions/${s.slug}`}
      data-testid={`service-card-${s.slug}`}
      className={`group flex h-full flex-col border border-charcoal/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lift ${
        featured ? "lg:grid lg:grid-cols-[1.1fr_1.4fr] lg:items-stretch" : ""
      }`}
    >
      <div className={`overflow-hidden ${featured ? "lg:h-full" : ""}`}>
        <img
          src={s.image}
          alt={s.imageAlt}
          width="1200"
          height="750"
          loading="lazy"
          className={`object-cover transition-transform duration-700 group-hover:scale-[1.03] ${
            featured ? "aspect-[16/10] w-full lg:aspect-auto lg:h-full" : "aspect-[16/10] w-full"
          }`}
        />
      </div>
      <div className={`flex flex-1 flex-col p-7 md:p-9 ${featured ? "lg:justify-center" : ""}`}>
        <p className="eyebrow text-river">{s.provider}</p>
        <h3 className={`mt-3 font-serif font-light tracking-tight text-navy ${featured ? "text-2xl sm:text-3xl" : "text-2xl"}`}>
          {s.name}
        </h3>
        <p className="mt-3 text-base leading-relaxed text-charcoal/80 sm:text-lg">{s.short}</p>
        <p className="mt-4 inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.1em] text-river">
          <span className="h-px w-6 bg-river" aria-hidden="true" />
          {s.benefit}
        </p>
        <span className="mt-5 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-forest">
          Learn more
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export default function Home() {
  const brandRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: brandRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-6%", "6%"]);

  const [first, ...rest] = SERVICES;

  return (
    <>
      <Seo
        title="Riverstone Partners — Business Solutions. Lasting Partnerships."
        description="Discover practical ways to grow revenue, improve efficiency, support your people, and explore new opportunities—with Riverstone Partners helping you take the next step."
        path="/"
      />

      {/* A — Cinematic hero */}
      <HeroVideo />

      {/* B — Introduction */}
      <section className="py-20 md:py-28" data-testid="home-intro-section">
        <div className="shell grid gap-10 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <p className="eyebrow text-forest">Introduction</p>
            <h2 className="mt-5 font-serif text-3xl font-light leading-[1.15] tracking-tight text-navy sm:text-4xl lg:text-[2.75rem]">
              The right solutions start with the right conversation.
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="lg:pt-14">
            <p className="text-lg leading-relaxed text-charcoal/80">
              Every business has different priorities. Riverstone Partners helps you
              explore solutions that align with yours—from strengthening your marketing
              and supporting your workforce to improving revenue operations and shipping
              efficiency.
            </p>
            <Link
              to="/approach"
              className="mt-7 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-forest underline-offset-4 hover:underline"
              data-testid="home-intro-approach-link"
            >
              See how it works <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* C — Services */}
      <section className="py-20 md:py-28" data-testid="home-services-section">
        <div className="shell">
          <Reveal className="max-w-3xl">
            <p className="eyebrow text-forest">Services</p>
            <h2 className="mt-5 font-serif text-3xl font-light leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Practical solutions. Meaningful possibilities.
            </h2>
          </Reveal>
          <div className="mt-12 space-y-6">
            <Reveal>
              <ServiceCard s={first} featured />
            </Reveal>
            <div className="grid gap-6 md:grid-cols-2">
              {rest.map((s, i) => (
                <Reveal key={s.slug} delay={i * 0.07} className={i === rest.length - 1 ? "md:col-span-2" : ""}>
                  <ServiceCard s={s} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* D — Interactive solution finder */}
      <SolutionFinder />

      {/* E — Our approach */}
      <section className="bg-sage/50 py-20 md:py-28" data-testid="home-approach-section">
        <div className="shell">
          <Reveal className="max-w-3xl">
            <p className="eyebrow text-forest">Our approach</p>
            <h2 className="mt-5 font-serif text-3xl font-light leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
              A clear path from conversation to action.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-10 md:grid-cols-3 md:gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1} className="relative">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-5xl font-light text-river/70">{i + 1}</span>
                  <div className="h-px flex-1 bg-forest/20" aria-hidden="true" />
                </div>
                <h3 className="mt-5 font-serif text-2xl text-navy">{s.title}</h3>
                <p className="mt-3 leading-relaxed text-charcoal/75">{s.body}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link to="/approach" className="btn-outline mt-12" data-testid="home-approach-link">
              Explore Our Approach
            </Link>
          </Reveal>
        </div>
      </section>

      {/* F — Relationship-focused brand section */}
      <section ref={brandRef} className="overflow-hidden py-20 md:py-28" data-testid="home-brand-section">
        <div className="shell grid items-center gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          <Reveal className="relative">
            <motion.img
              src={IMAGES.riverStones}
              alt="A clear river flowing over stones of different sizes in sunlight, surrounded by trees"
              width="1264"
              height="848"
              loading="lazy"
              style={{ y: imgY }}
              className="aspect-[4/3] w-full scale-[1.12] object-cover shadow-soft"
            />
            <div className="absolute -bottom-5 -right-3 hidden bg-forest px-7 py-5 text-ivory md:block" aria-hidden="true">
              <p className="font-serif text-lg leading-snug">Rivers carve. Stone remains.</p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="eyebrow text-forest">Who we are</p>
            <h2 className="mt-5 font-serif text-3xl font-light leading-tight tracking-tight text-navy sm:text-4xl">
              Built around your business. Grounded in relationships.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-charcoal/80">
              Riverstone Partners connects businesses with specialized solution
              providers—then stays engaged to make sure the relationship works. We start
              by understanding your priorities, and we only suggest what might genuinely
              fit.
            </p>
            <p className="mt-4 leading-relaxed text-charcoal/75">
              To be clear about roles: the specialists we introduce—marketing teams,
              wellness providers, billing experts, shipping analysts—deliver the actual
              work. Our role is to understand your business, make the right introduction,
              and help you move forward with confidence.
            </p>
          </Reveal>
        </div>
      </section>

      {/* G — Affiliate opportunity */}
      <section className="border-y border-charcoal/10 bg-mist py-20 md:py-24" data-testid="home-partner-section">
        <div className="shell grid items-center gap-10 lg:grid-cols-[1.4fr_1fr]">
          <Reveal>
            <p className="eyebrow text-river">The Aspire Partners opportunity</p>
            <h2 className="mt-5 max-w-xl font-serif text-3xl font-light leading-tight tracking-tight text-navy sm:text-4xl">
              Put your professional relationships to work.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-charcoal/80">
              Explore the Aspire Partners opportunity and learn how introducing
              businesses to relevant solutions can become part of your professional path.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:justify-self-end">
            <Link to="/become-a-partner" className="btn-primary" data-testid="home-partner-cta">
              Explore the Partner Opportunity
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* H — Final CTA (bright, welcoming) */}
      <section className="border-t border-charcoal/10 bg-sage/60 py-20 text-navy md:py-28" data-testid="home-final-cta">
        <div className="shell max-w-3xl text-center">
          <Reveal>
            <p className="eyebrow text-forest">Next step</p>
            <h2 className="mt-5 font-serif text-3xl font-light leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Let’s find your next step forward.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-charcoal/75">
              Tell us what you’re working toward. We’ll help you explore the solutions
              that may fit.
            </p>
            <Link to="/contact" className="btn-primary mt-9" data-testid="home-final-cta-button">
              Start a Conversation
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
